import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill } from "../../components/common/Primitives";
import { Loader2, RefreshCw, Filter, X, Mail, AlertTriangle, CheckCircle2, MousePointerClick, Eye, Search, Send } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const STATUS_TONE = {
  sent: "success",
  failed: "danger",
};
const DELIVERY_TONE = {
  delivered: "success",
  opened: "gold",
  clicked: "gold",
  bounced: "danger",
  complained: "danger",
  delayed: "warn",
};

function StatTile({ label, value, tone = "muted", testId }) {
  return (
    <div className="rounded border border-navy/10 bg-white px-4 py-3" data-testid={testId}>
      <div className="text-[10px] tracking-widest uppercase text-ink-muted">{label}</div>
      <div className={`text-2xl font-heading mt-1 ${tone === "danger" ? "text-rose-600" : tone === "success" ? "text-emerald-600" : "text-ink"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

export default function AdminEmailLog() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", delivery_status: "", template: "", to: "", q: "" });
  const [skip, setSkip] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [retryingId, setRetryingId] = useState(null);
  const limit = 25;

  const load = async (overrides) => {
    setLoading(true);
    try {
      const active = overrides ?? filters;
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      for (const [k, v] of Object.entries(active)) if (v) params.append(k, v);
      const [logRes, statRes, tmplRes] = await Promise.all([
        api.get(`/admin/email-log?${params.toString()}`),
        api.get("/admin/email-log/stats"),
        api.get("/admin/email-log/templates/summary"),
      ]);
      setItems(logRes.data.items || []);
      setTotal(logRes.data.total || 0);
      setStats(statRes.data);
      setTemplates(tmplRes.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [skip]);

  const applyFilters = () => {
    // If skip is already 0 the useEffect won't fire, so refetch manually.
    if (skip !== 0) setSkip(0);
    else load();
  };
  const clearFilters = () => {
    const empty = { status: "", delivery_status: "", template: "", to: "", q: "" };
    setFilters(empty);
    if (skip !== 0) setSkip(0);
    else load(empty);
  };

  const retry = async (eventId) => {
    setRetryingId(eventId);
    try {
      const res = await api.post(`/admin/email-log/${eventId}/retry`);
      if (res.data?.result?.sent) toast.success("Resend queued.");
      else toast.warning(`Retry failed: ${res.data?.result?.reason || "unknown"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setRetryingId(null); }
  };

  return (
    <DashboardShell
      title="Email Delivery Log"
      subtitle="ADMIN · TRANSACTIONAL EMAIL AUDIT"
      actions={
        <Button variant="ghost" onClick={load} disabled={loading} data-testid="email-log-refresh">
          {loading ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <RefreshCw size={12} className="inline mr-1" />}
          Refresh
        </Button>
      }
    >
      <Toaster richColors position="top-right" />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatTile label="Total logged" value={stats.total} testId="stat-total" />
          <StatTile label="Sent (24h)" value={stats.last_24h?.sent} tone="success" testId="stat-sent-24h" />
          <StatTile label="Failed (24h)" value={stats.last_24h?.failed} tone={stats.last_24h?.failed ? "danger" : "muted"} testId="stat-failed-24h" />
          <StatTile label="Bounced (7d)" value={stats.last_7d?.bounced} tone={stats.last_7d?.bounced ? "danger" : "muted"} testId="stat-bounced-7d" />
        </div>
      )}

      <Card testId="email-log-filters" className="mb-4">
        <div className="overline mb-3 flex items-center gap-2"><Filter size={12} /> Filters</div>
        <div className="grid gap-2 md:grid-cols-5">
          <input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
            placeholder="Search subject / recipient / template…"
            className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none md:col-span-2"
            data-testid="filter-q"
          />
          <select
            value={filters.template}
            onChange={(e) => setFilters({ ...filters, template: e.target.value })}
            className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
            data-testid="filter-template"
          >
            <option value="">Template: any</option>
            {templates.map((t) => (
              <option key={t.template} value={t.template}>{t.template} ({t.count})</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
            data-testid="filter-status"
          >
            <option value="">Send: any</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={filters.delivery_status}
            onChange={(e) => setFilters({ ...filters, delivery_status: e.target.value })}
            className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
            data-testid="filter-delivery-status"
          >
            <option value="">Delivery: any</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="bounced">Bounced</option>
            <option value="complained">Complained</option>
          </select>
          <input
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            placeholder="Recipient contains…"
            className="text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
            data-testid="filter-to"
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={applyFilters} data-testid="filter-apply">
              <Search size={12} className="inline mr-1" /> Apply
            </Button>
            <Button variant="ghost" onClick={clearFilters} data-testid="filter-clear"><X size={12} className="inline mr-1" /> Clear</Button>
          </div>
        </div>
      </Card>

      {items === null && (
        <div className="text-ink-muted text-sm py-6"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
      )}

      {items && items.length === 0 && (
        <Card testId="email-log-empty">
          <div className="text-sm text-ink-muted py-4 text-center">No emails logged for these filters.</div>
        </Card>
      )}

      {items && items.length > 0 && (
        <Card className="p-0 overflow-hidden" testId="email-log-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-ink-muted bg-navy/[0.02]">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">To</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Template</th>
                  <th className="text-left px-3 py-2">Send</th>
                  <th className="text-left px-3 py-2">Delivery</th>
                  <th className="text-left px-3 py-2">Reason / Resend ID</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.event_id} className="border-t border-navy/[0.06]" data-testid={`email-log-row-${r.event_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-soft whitespace-nowrap">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink">{r.to}</td>
                    <td className="px-3 py-2 text-ink-soft max-w-xs truncate" title={r.subject}>{r.subject}</td>
                    <td className="px-3 py-2">{r.template ? <Pill tone="muted">{r.template}</Pill> : <span className="text-ink-faint">—</span>}</td>
                    <td className="px-3 py-2"><Pill tone={STATUS_TONE[r.status] || "muted"}>{r.status}</Pill></td>
                    <td className="px-3 py-2">
                      {r.delivery_status
                        ? <Pill tone={DELIVERY_TONE[r.delivery_status] || "muted"}>
                            {r.delivery_status === "delivered" && <CheckCircle2 size={10} className="inline mr-1" />}
                            {r.delivery_status === "opened" && <Eye size={10} className="inline mr-1" />}
                            {r.delivery_status === "clicked" && <MousePointerClick size={10} className="inline mr-1" />}
                            {r.delivery_status === "bounced" && <AlertTriangle size={10} className="inline mr-1" />}
                            {r.delivery_status}
                          </Pill>
                        : <span className="text-ink-faint text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-ink-soft max-w-xs truncate" title={r.reason || r.resend_id || ""}>
                      {r.reason || r.resend_id || "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.has_html ? (
                        <button
                          onClick={() => retry(r.event_id)}
                          disabled={retryingId === r.event_id}
                          className="text-[11px] px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`email-retry-${r.event_id}`}
                        >
                          {retryingId === r.event_id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                          {retryingId === r.event_id ? "Sending…" : "Retry"}
                        </button>
                      ) : (
                        <span className="text-[10px] text-ink-faint italic">No html on record</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-navy/[0.06] text-xs text-ink-soft">
            <span>Showing {items.length} of {total}</span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="page-prev">Previous</Button>
              <Button variant="ghost" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="page-next">Next</Button>
            </div>
          </div>
        </Card>
      )}
    </DashboardShell>
  );
}
